import { Platform } from 'react-native';

/**
 * Web-compatible storage adapter that uses browser localStorage 
 * when AsyncStorage is not available (web environment)
 */
export class WebCompatibleStorage {
  private static instance: WebCompatibleStorage;
  
  private constructor() {}
  
  public static getInstance(): WebCompatibleStorage {
    if (!WebCompatibleStorage.instance) {
      WebCompatibleStorage.instance = new WebCompatibleStorage();
    }
    return WebCompatibleStorage.instance;
  }

  async setItem(key: string, value: string): Promise<void> {
    try {
      if (Platform.OS === 'web' || typeof window !== 'undefined') {
        // Web environment - use localStorage
        localStorage.setItem(key, value);
      } else {
        // React Native environment - use AsyncStorage
        const AsyncStorage = require('@react-native-async-storage/async-storage').default;
        await AsyncStorage.setItem(key, value);
      }
    } catch (error) {
      console.error('❌ WebCompatibleStorage setItem error:', error);
      throw error;
    }
  }

  async getItem(key: string): Promise<string | null> {
    try {
      if (Platform.OS === 'web' || typeof window !== 'undefined') {
        // Web environment - use localStorage
        return localStorage.getItem(key);
      } else {
        // React Native environment - use AsyncStorage
        const AsyncStorage = require('@react-native-async-storage/async-storage').default;
        return await AsyncStorage.getItem(key);
      }
    } catch (error) {
      console.error('❌ WebCompatibleStorage getItem error:', error);
      return null;
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      if (Platform.OS === 'web' || typeof window !== 'undefined') {
        // Web environment - use localStorage
        localStorage.removeItem(key);
      } else {
        // React Native environment - use AsyncStorage
        const AsyncStorage = require('@react-native-async-storage/async-storage').default;
        await AsyncStorage.removeItem(key);
      }
    } catch (error) {
      console.error('❌ WebCompatibleStorage removeItem error:', error);
      throw error;
    }
  }

  async clear(): Promise<void> {
    try {
      if (Platform.OS === 'web' || typeof window !== 'undefined') {
        // Web environment - use localStorage
        localStorage.clear();
      } else {
        // React Native environment - use AsyncStorage
        const AsyncStorage = require('@react-native-async-storage/async-storage').default;
        await AsyncStorage.clear();
      }
    } catch (error) {
      console.error('❌ WebCompatibleStorage clear error:', error);
      throw error;
    }
  }

  async getAllKeys(): Promise<string[]> {
    try {
      if (Platform.OS === 'web' || typeof window !== 'undefined') {
        // Web environment - use localStorage
        const keys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key) keys.push(key);
        }
        return keys;
      } else {
        // React Native environment - use AsyncStorage
        const AsyncStorage = require('@react-native-async-storage/async-storage').default;
        return await AsyncStorage.getAllKeys();
      }
    } catch (error) {
      console.error('❌ WebCompatibleStorage getAllKeys error:', error);
      return [];
    }
  }
}

// Export singleton instance
export const webStorage = WebCompatibleStorage.getInstance();